import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Box, Typography, Grid, Button } from '@material-ui/core';
import { Skeleton, Alert } from '@material-ui/lab';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { FormattedMessage } from 'react-intl';
import { makeStyles } from '@material-ui/core/styles';
import { useContentStyles } from './styles';

const chevronRightIcon = <ChevronRightIcon />;

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(2)
  },
  alert: {
    alignItems: 'center',
    width: '100%'
  },
  summaryBox: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius
  },
  infoBox: {
    backgroundColor: theme.palette.grey[100],
    border: `1px solid ${theme.palette.grey[100]}`,
    marginBottom: theme.spacing(2)
  },
  title: {
    fontWeight: theme.typography.fontWeightBold
  },
  expandButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end'
  },
  expandButton: {
    padding: 0
  },
  skeletonSpacing: {
    marginTop: theme.spacing(1)
  },
  fromContainer: {
    marginTop: theme.spacing(1),
    display: 'flex'
  },
  fromLabelText: {
    flexShrink: 0,
    minWidth: '48px',
    marginRight: theme.spacing(1)
  }
}));

const ProjectTimeEntrySummary = ({
  projectName,
  taskDisplayText,
  loading,
  count,
  totalHours,
  showExpandButton = false
}) => {
  const classes = useStyles();
  const contentClasses = useContentStyles();
  const movingTimeEntriesCount = useMemo(
    () => ({
      count,
      hours: totalHours.toFixed(2),
      bold: chunks => <strong>{chunks}</strong>
    }),
    [count, totalHours]
  );

  if (loading)
    return (
      <Box className={clsx(classes.summaryBox, classes.infoBox)}>
        <Skeleton variant="text" width="80%" height={20} />
        <Skeleton
          variant="text"
          width="60%"
          height={20}
          className={classes.skeletonSpacing}
        />
      </Box>
    );

  if (!taskDisplayText)
    return (
      <Box className={clsx(classes.summaryBox, classes.infoBox)}>
        <Typography variant="body1">
          <FormattedMessage id="moveTimeDialog.selectTaskMessage" />
        </Typography>
      </Box>
    );

  if (count <= 0)
    return (
      <Grid container spacing={0}>
        <div className={classes.root}>
          <Alert
            severity="error"
            className={classes.alert}
            data-qe-id="ErrorMessage"
            icon={false}
          >
            <Typography component="div" className={classes.title}>
              <FormattedMessage id="moveTimeDialog.noTimeEntries" />
            </Typography>
            <Typography component="div">
              <FormattedMessage id="moveTimeDialog.noTimeEntriesMessage" />
            </Typography>
          </Alert>
        </div>
      </Grid>
    );

  return (
    <Box className={clsx(classes.summaryBox, classes.infoBox)}>
      <Typography variant="body1">
        <FormattedMessage
          id="moveTimeDialog.movingTimeEntriesDescription"
          values={movingTimeEntriesCount}
        />
      </Typography>
      {showExpandButton && (
        <div className={classes.expandButtonContainer}>
          <Button
            endIcon={chevronRightIcon}
            color="primary"
            className={classes.expandButton}
          >
            <FormattedMessage id="moveTimeDialog.expand" />
          </Button>
        </div>
      )}
      <div className={classes.fromContainer}>
        <Typography variant="body1" className={classes.fromLabelText}>
          <FormattedMessage id="moveTimeDialog.from" />:
        </Typography>
        <Typography variant="body1" className={contentClasses.fromLabel}>
          {projectName} / {taskDisplayText}
        </Typography>
      </div>
    </Box>
  );
};

ProjectTimeEntrySummary.propTypes = {
  projectName: PropTypes.string,
  taskDisplayText: PropTypes.string,
  loading: PropTypes.bool,
  count: PropTypes.number,
  totalHours: PropTypes.number,
  showExpandButton: PropTypes.bool
};

export default ProjectTimeEntrySummary;
